import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { produce } from 'immer';
import { concatMap, first, map, type Observable, of, withLatestFrom } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { type CanvasElementId, type CanvasNamespaceId } from '@blaze-runtime/engine-domain';
import { type DataBindingSourceInfo } from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { type BaseSchema } from '@blaze-shared/schema-domain';
import {
  type Connection,
  isMergeNode,
  spliceWorkflowNodes,
  type Workflow,
} from '@blaze-shared/workflow-domain';

import { RuntimeFacade, type State } from '../../+state';
import { DEFAULT_SUCCESS_MESSAGE } from '../../elements/form-container/form-container.model';
import {
  findSubmitWorkflowConnection,
  getOrCreateConnection,
  getUiRuntimeElement,
  getWorkflowRuntimeElement,
} from '../../utils/runtime';
import { WorkflowCrudService } from '../workflow-crud.service';

import { MergeActionNodeService } from './merge-action-node.service';

export interface FormListSubmitWorkflow {
  buttonConnection: Connection;
  formConnection: Connection;
  workflow: Workflow;
}

export interface AvailabilitySubmitWorkflow {
  formConnection: Connection;
  workflow: Workflow;
}

/** What the element submits, and what says when it submits. */
interface SubmitWorkflowRequest {
  /** Absent for an element that submits on change alone. */
  buttonId?: CanvasElementId;
  elementDataSource: DataBindingSourceInfo;
  elementId: CanvasElementId;
  isServer: boolean;
  namespaceId: CanvasNamespaceId;
  projectId: string;
  /** What the user is told once the rows are saved; nothing when the element says nothing. */
  successMessage?: string;
}

interface SubmitWorkflowUpdate {
  buttonConnection: Connection | undefined;
  formConnection: Connection;
  workflow: Workflow;
}

/**
 * Creates — or brings up to date — the workflow that saves what an element collected.
 *
 * The element's own output connection is what identifies the workflow, so an element that already
 * submits somewhere keeps that workflow rather than gaining a second one. The workflow does not
 * autostart: it runs when the user submits, not when the page loads.
 *
 * **Note:** neither the workflow nor the connections are put into the state here.
 */
@Injectable({ providedIn: 'root' })
export class RowListSubmitWorkflowService {
  private readonly mergeActionNodeService = inject(MergeActionNodeService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store<State>);
  private readonly workflowCrudService = inject(WorkflowCrudService);

  /**
   * @param listId the form list whose rows are submitted
   * @param buttonId the button that submits them
   * @returns nothing when there is no project or no submit button, in which case there is nothing
   * to wire up.
   */
  createOrUpdateFormListSubmitWorkflowAndConnections(
    projectId: string | undefined,
    namespaceId: CanvasNamespaceId,
    listId: CanvasElementId,
    buttonId: CanvasElementId | undefined,
    elementDataSource: DataBindingSourceInfo,
    isServer = false,
  ): Observable<FormListSubmitWorkflow | undefined> {
    if (!projectId || !buttonId) {
      return of(undefined);
    }
    return this.createOrUpdate({
      buttonId,
      elementDataSource,
      elementId: listId,
      isServer,
      namespaceId,
      projectId,
      // The list is the only thing a form list submits; the button just says when.
      successMessage: DEFAULT_SUCCESS_MESSAGE,
    }).pipe(
      map(({ buttonConnection, formConnection, workflow }) =>
        buttonConnection ? { buttonConnection, formConnection, workflow } : undefined,
      ),
    );
  }

  /**
   * The availability element submits on change alone — it has no separate submit button.
   * @returns nothing when there is no project to add the workflow to.
   */
  createOrUpdateAvailabilitySubmitWorkflowAndConnections(
    projectId: string | undefined,
    namespaceId: CanvasNamespaceId,
    elementId: CanvasElementId,
    elementDataSource: DataBindingSourceInfo,
    isServer = false,
  ): Observable<AvailabilitySubmitWorkflow | undefined> {
    if (!projectId) {
      return of(undefined);
    }
    return this.createOrUpdate({
      elementDataSource,
      elementId,
      isServer,
      namespaceId,
      projectId,
    }).pipe(map(({ formConnection, workflow }) => ({ formConnection, workflow })));
  }

  /**
   * Wires the element — and its submit button when it has one — to a workflow that merges what it
   * collected back into its data source.
   */
  private createOrUpdate({
    buttonId,
    elementDataSource,
    elementId,
    isServer,
    namespaceId,
    projectId,
    successMessage,
  }: SubmitWorkflowRequest): Observable<SubmitWorkflowUpdate> {
    return this.existingOrNewWorkflow(elementId, buttonId, projectId, namespaceId, isServer).pipe(
      concatMap((workflow) => this.withDataSourceSchema(workflow, elementDataSource)),
      withLatestFrom(this.store.select(runtimeSelectors.selectConnections)),
      concatMap(([{ schema, workflow }, connections]) => {
        const formConnection = getOrCreateConnection(
          connections,
          namespaceId,
          getUiRuntimeElement(elementId),
          getWorkflowRuntimeElement(workflow.id),
          'change',
        );
        const buttonConnection = buttonId
          ? getOrCreateConnection(
              connections,
              namespaceId,
              getUiRuntimeElement(buttonId),
              getWorkflowRuntimeElement(workflow.id),
              'click',
            )
          : undefined;

        const updatedWorkflow = produce(workflow, (draft) => {
          draft.preventAutoStart = true;
          draft.inputs = {
            ...draft.inputs,
            input1: formConnection.id,
            form: formConnection.id,
            ...(buttonConnection ? { button: buttonConnection.id } : {}),
          };
          this.spliceMergeActionNode(draft, elementDataSource, successMessage);
        });

        const inputSchemaMapping: { [key: string]: BaseSchema | undefined } = {
          form: schema,
          input1: schema,
          // The button carries no data of its own, and says so rather than being left out.
          ...(buttonConnection ? { button: undefined } : {}),
        };
        return this.workflowCrudService
          .updateWorkflow(
            updatedWorkflow,
            [formConnection, ...(buttonConnection ? [buttonConnection] : [])],
            inputSchemaMapping,
          )
          .pipe(
            map((savedWorkflow) => ({
              buttonConnection,
              formConnection,
              workflow: savedWorkflow,
            })),
          );
      }),
    );
  }

  /** The workflow the element already submits to, or a fresh empty one. */
  private existingOrNewWorkflow(
    elementId: CanvasElementId,
    commitButtonId: CanvasElementId | undefined,
    projectId: string,
    namespaceId: CanvasNamespaceId,
    isServer: boolean,
  ): Observable<Workflow> {
    const outputConnection = findSubmitWorkflowConnection(
      this.runtimeFacade.connectionsValue,
      elementId,
      commitButtonId,
    );
    const existingWf = outputConnection
      ? this.runtimeFacade.workflowsValue.find((wf) => wf.id === outputConnection.target.id)
      : undefined;

    return existingWf
      ? of(existingWf)
      : this.workflowCrudService.createWorkflow({
          inputs: {},
          inputSchemas: [],
          isLongRunning: false,
          isServer,
          nodes: [],
          pageId: namespaceId,
          projectId,
          startNodeId: null,
        });
  }

  /** The schema of what the element collected, which becomes the workflow's input schema. */
  private withDataSourceSchema(
    workflow: Workflow,
    elementDataSource: DataBindingSourceInfo,
  ): Observable<{ schema: BaseSchema | undefined; workflow: Workflow }> {
    return this.schemaService
      .get(elementDataSource.id, elementDataSource.sourceType === 'context')
      .pipe(
        first(),
        map((schema) => ({ schema, workflow })),
      );
  }

  /** Replaces the workflow's action node with a freshly generated merge into the data source. */
  private spliceMergeActionNode(
    draft: Workflow,
    elementDataSource: DataBindingSourceInfo,
    successMessage?: string,
  ): void {
    const node = draft.nodes.find(({ id }) => id === 'action');
    spliceWorkflowNodes(draft, node ?? null, [
      this.mergeActionNodeService.getMergeActionNode(
        elementDataSource,
        isMergeNode(node) ? node : undefined,
        'form',
        successMessage,
      ),
    ]);
  }
}
